import * as React from 'react';
import { useCallback } from 'react';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import { useMasterContext } from '&/master';
import { useTenantProfile } from '../../hooks/use-tenant-profile';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TenantRowProps {
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isActivationKey(key: string): boolean {
  return key === 'Enter' || key === ' ';
}

function buildRowClassName(canSelect: boolean, isActive: boolean): string {
  return cn(
    'flex items-center gap-2 px-3 py-2 w-full transition-colors text-left border-y border-border',
    canSelect ? 'group/tenant-row cursor-pointer' : 'cursor-default',
    canSelect && (isActive ? 'bg-accent' : 'hover:bg-muted'),
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TenantBadgeProps {
  isActive: boolean;
}

function TenantBadge({ isActive }: TenantBadgeProps): React.JSX.Element {
  const { t } = useTranslation('common');

  if (isActive) {
    return (
      <span className="text-[9px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
        {t('mainSelector.current')}
      </span>
    );
  }

  return (
    <span className="text-[11px] text-primary font-medium hidden group-hover/tenant-row:inline shrink-0">
      {t('mainSelector.select')}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TenantRow({ onClose }: TenantRowProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { hasPermission } = usePermissions();
  const { clearScope, siteId, spaceId } = useMasterContext();
  const { data: tenant } = useTenantProfile();
  const canSelectTenant = hasPermission('tenants:read');
  const isActive = !spaceId && !siteId;

  const handleClick = useCallback((): void => {
    clearScope();
    onClose();
  }, [clearScope, onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (isActivationKey(event.key)) {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const interactiveProps = canSelectTenant
    ? { onClick: handleClick, onKeyDown: handleKeyDown, role: 'button' as const, tabIndex: 0 }
    : {};

  return (
    <div className={buildRowClassName(canSelectTenant, isActive)} {...interactiveProps}>
      <div className="flex size-5 items-center justify-center rounded bg-primary shrink-0">
        <span className="text-xs font-bold text-primary-foreground">
          {tenant?.name?.charAt(0).toUpperCase() ?? 'T'}
        </span>
      </div>
      <span className="text-sm font-medium truncate flex-1">{tenant?.name ?? t('mainSelector.tenant')}</span>
      {canSelectTenant && <TenantBadge isActive={isActive} />}
    </div>
  );
}
